import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, NavLink, useLocation } from 'react-router-dom';
import { CalendarDays, Calculator, LayoutDashboard, LogOut, Download, X, RefreshCw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useAudience } from '@/context/AudienceContext';
import AdminPage from '@/pages/AdminPage';
import Wrapulator from '@/components/Wrapulator';
import MobileBookingsList from '@/components/MobileBookingsList';
import PushToggle from '@/components/PushToggle';

const TABS = [
  { to: '/m/bookings',   label: 'Bookings',   icon: CalendarDays },
  { to: '/m/wrapulator', label: 'Wrapulator', icon: Calculator },
  { to: '/m/dashboard',  label: 'Dashboard',  icon: LayoutDashboard },
];

const TITLES = { '/m/bookings': 'Bookings', '/m/wrapulator': 'Wrapulator', '/m/dashboard': 'Admin dashboard' };

// Admin-only installable shell (PWA). No marketing header/footer — just the three tools.
export default function MobileAdminApp() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [installEvt, setInstallEvt] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('mi_pwa_hint') === '1');
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone;

  useEffect(() => {
    if ('serviceWorker' in navigator) navigator.serviceWorker.register('/sw.js').catch(() => {});
    const onPrompt = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const install = async () => {
    if (!installEvt) return;
    installEvt.prompt();
    await installEvt.userChoice;
    setInstallEvt(null);
  };
  const hideHint = () => { localStorage.setItem('mi_pwa_hint', '1'); setDismissed(true); };
  const title = Object.entries(TITLES).find(([k]) => pathname.startsWith(k))?.[1] || 'Motor iNKS Admin';

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col" data-testid="mobile-admin-app"
         style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      <header className="sticky top-0 z-40 bg-[#050505]/95 backdrop-blur border-b mi-line-strong px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <img src="/pwa/icon-192.png" alt="" className="w-8 h-8 rounded-sm" />
          <div className="min-w-0">
            <div className="mi-headline text-lg leading-none truncate" data-testid="mobile-admin-title">{title}</div>
            <div className="mi-eyebrow text-[9px] text-white/40 truncate">ADMIN · {user?.email}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <PushToggle />
          <button onClick={() => window.location.reload()} className="p-2 text-white/50 hover:text-white" aria-label="Refresh" data-testid="mobile-admin-refresh"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={logout} className="p-2 text-white/50 hover:text-[#E63946]" aria-label="Sign out" data-testid="mobile-admin-logout"><LogOut className="w-4 h-4" /></button>
        </div>
      </header>

      {!standalone && !dismissed && (
        <div className="relative z-10 mx-4 mt-3 border border-[#E63946]/40 bg-[#E63946]/[0.06] p-3 text-xs flex items-start gap-3" data-testid="mobile-install-hint">
          <Download className="w-4 h-4 text-[#E63946] flex-shrink-0 mt-0.5" />
          <div className="flex-1 leading-relaxed">
            <strong className="text-white">Add to your home screen</strong> for a full-screen app.
            {installEvt
              ? <button onClick={install} className="ml-2 text-[#E63946] underline" data-testid="mobile-install-btn">Install now</button>
              : <span className="text-white/60"> iPhone: Share → "Add to Home Screen". Android: menu ⋮ → "Install app".</span>}
          </div>
          <button onClick={hideHint} className="text-white/40 hover:text-white" aria-label="Dismiss"><X className="w-4 h-4" /></button>
        </div>
      )}

      <main className="flex-1 pb-24">
        <Routes>
          <Route index element={<Navigate to="/m/bookings" replace />} />
          <Route path="bookings" element={<MobileBookingsList />} />
          <Route path="wrapulator" element={<MobileWrapulator />} />
          <Route path="dashboard" element={<AdminPage embed />} />
          <Route path="*" element={<Navigate to="/m/bookings" replace />} />
        </Routes>
      </main>

      <nav className="fixed bottom-0 inset-x-0 z-40 bg-[#0A0A0A] border-t mi-line-strong grid grid-cols-3"
           style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} data-testid="mobile-admin-nav">
        {TABS.map((t) => (
          <NavLink key={t.to} to={t.to} data-testid={`mobile-nav-${t.label.toLowerCase()}`}
                   className={({ isActive }) => `flex flex-col items-center justify-center gap-1 py-3 text-[10px] mi-eyebrow transition-colors ${isActive ? 'text-[#E63946]' : 'text-white/50 hover:text-white'}`}>
            <t.icon className="w-5 h-5" /> {t.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

function MobileWrapulator() {
  const { setAudience } = useAudience();
  // Admin tool → always the B2B/wholesale-capable view.
  useEffect(() => { setAudience?.('b2b'); }, [setAudience]);
  return <div className="-mt-20"><Wrapulator /></div>;
}
